import React, { useState, useEffect } from 'react';
import api from '../services/api';

/**
 * Section URSSAF & DSN - dashboard client.
 * Timeline des déclarations + génération DSN XML + enregistrement paiement.
 *
 * Gate backend : PlanChecker(module="social", min_plan="pro")
 */
const URSSAFSection = () => {
  const [declarations, setDeclarations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [moisSelectionne, setMoisSelectionne] = useState(new Date().toISOString().slice(0, 7));
  const [actionLoading, setActionLoading] = useState({});

  const loadDeclarations = async () => {
    setLoading(true);
    setError(null);
    const resp = await api.get('/social/urssaf');
    if (resp && resp.success) {
      setDeclarations(resp.data || []);
    } else if (resp && (resp.detail || resp.error)) {
      setError(resp.detail || resp.error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadDeclarations();
  }, []);

  // Générer + télécharger DSN XML
  const handleGenererDSN = async () => {
    setActionLoading({ ...actionLoading, dsn: true });
    try {
      const resp = await api.post('/paie/dsn/' + moisSelectionne, null, { responseType: 'blob' });
      if (resp instanceof Blob) {
        const url = URL.createObjectURL(resp);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dsn_${moisSelectionne}.xml`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        alert('✅ DSN XML générée et téléchargée pour ' + moisSelectionne);
        await loadDeclarations();
      } else if (resp && (resp.detail || resp.error)) {
        const msg = resp.detail || resp.error;
        if (msg.includes('404') || msg.includes('salarié')) {
          alert('⚠️ Aucun salarié actif. Générez une DSN néant depuis la section Salariés.');
        } else if (msg.includes('403') || msg.includes('plan')) {
          alert('🔒 Plan Pro requis pour générer la DSN. Rendez-vous dans "Mon Abonnement".');
        } else {
          alert('❌ ' + msg);
        }
      }
    } catch (e) {
      alert('❌ ' + e.message);
    }
    setActionLoading({ ...actionLoading, dsn: false });
  };

  // Marquer une déclaration comme payée
  const handlePayer = async (declaration) => {
    const datePaiement = window.prompt(
      `Confirmer le paiement URSSAF de ${declaration.montant_total.toFixed(2).replace('.', ',')}€\nDate de paiement (AAAA-MM-JJ) :`,
      new Date().toISOString().slice(0, 10)
    );
    if (!datePaiement) return;

    const reference = window.prompt('Référence virement (optionnel) :', '') || null;

    setActionLoading({ ...actionLoading, ['pay-' + declaration.id]: true });
    const resp = await api.post('/social/urssaf/payer', {
      declaration_id: declaration.id,
      date_paiement: datePaiement,
      reference: reference,
    });
    if (resp && resp.success) {
      alert('✅ ' + resp.message);
      await loadDeclarations();
    } else {
      alert('❌ ' + ((resp && (resp.detail || resp.error)) || 'Erreur'));
    }
    setActionLoading({ ...actionLoading, ['pay-' + declaration.id]: false });
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#73726C' }}>Chargement des déclarations...</div>;
  }

  if (error && (error.includes('403') || error.includes('plan'))) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>Plan Pro requis</h2>
        <p style={{ color: '#73726C', marginBottom: '24px' }}>
          La gestion URSSAF et la génération DSN sont disponibles à partir du plan Pro.
        </p>
        <p style={{ fontSize: '13px', color: '#73726C' }}>
          💡 Améliorez votre plan dans "💳 Mon Abonnement".
        </p>
      </div>
    );
  }

  const declarationsRetard = declarations.filter((d) => d.en_retard);

  return (
    <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      {/* Header */}
      <h2 style={{ fontSize: '20px', fontWeight: '600', margin: '0 0 8px' }}>🏛️ URSSAF & DSN</h2>
      <p style={{ fontSize: '13px', color: '#73726C', margin: '0 0 24px' }}>
        Générez vos Déclarations Sociales Nominatives (DSN) mensuelles et suivez les paiements URSSAF.
      </p>

      {/* Alerte retard */}
      {declarationsRetard.length > 0 && (
        <div
          style={{
            background: '#FAEEDA',
            border: '1px solid #D85A30',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '20px',
            fontSize: '14px',
            color: '#D85A30',
          }}
        >
          ⚠️ <strong>{declarationsRetard.length} déclaration(s) en retard</strong>. Pensez à régulariser rapidement pour éviter les pénalités (7,50€ par salarié manquant).
        </div>
      )}

      {/* Carte génération DSN */}
      <div
        style={{
          background: 'white',
          border: '1px solid #E8E7E3',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px',
        }}
      >
        <h3 style={{ fontSize: '14px', fontWeight: '600', margin: '0 0 12px' }}>📋 Générer une DSN</h3>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '13px', color: '#2D2D2D' }}>Mois :</label>
          <input
            type="month"
            value={moisSelectionne}
            onChange={(e) => setMoisSelectionne(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #E8E7E3',
              borderRadius: '8px',
              fontSize: '13px',
              background: 'white',
            }}
          />
          <button onClick={handleGenererDSN} disabled={actionLoading.dsn} style={btnPrimary}>
            {actionLoading.dsn ? '⏳ Génération...' : '📥 Générer + Télécharger XML'}
          </button>
        </div>
        <p style={{ fontSize: '11px', color: '#73726C', marginTop: '12px', lineHeight: '1.5' }}>
          💡 La DSN générée est un fichier XML à déposer sur{' '}
          <a href="https://www.net-entreprises.fr" target="_blank" rel="noreferrer" style={{ color: '#0F6E56' }}>
            net-entreprises.fr
          </a>
          . En plan Expert, le dépôt peut être automatisé via le Copilote.
        </p>
      </div>

      {/* Timeline déclarations */}
      <div
        style={{
          background: 'white',
          border: '1px solid #E8E7E3',
          borderRadius: '12px',
          padding: '20px',
        }}
      >
        <h3 style={{ fontSize: '14px', fontWeight: '600', margin: '0 0 16px' }}>
          📅 Historique des déclarations (24 derniers mois)
        </h3>

        {declarations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px', color: '#73726C' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📭</div>
            <p>Aucune déclaration URSSAF enregistrée.</p>
            <p style={{ fontSize: '12px' }}>
              Pour générer une déclaration : validez d'abord les paies dans "👥 Salariés & Paie", puis cliquez sur "Générer DSN" ci-dessus.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {declarations.map((d) => (
              <div
                key={d.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto auto',
                  gap: '12px',
                  alignItems: 'center',
                  padding: '12px',
                  borderRadius: '8px',
                  background: d.en_retard ? '#FFF5F0' : (d.statut === 'paye' ? '#F0FAF5' : '#FAF9F5'),
                  border: '1px solid #E8E7E3',
                }}
              >
                <div style={{ fontSize: '24px' }}>{d.badge}</div>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '14px' }}>
                    DSN {d.periode}
                    {d.en_retard && (
                      <span style={{ marginLeft: '8px', fontSize: '11px', color: '#D85A30', fontWeight: '600' }}>
                        RETARD ({d.jours_retard}j)
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#73726C' }}>
                    Échéance : {d.date_echeance ? new Date(d.date_echeance).toLocaleDateString('fr-FR') : '—'}
                    {d.date_paiement && ` · Payée le ${new Date(d.date_paiement).toLocaleDateString('fr-FR')}`}
                  </div>
                </div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: d.en_retard ? '#D85A30' : '#2D2D2D' }}>
                  {d.montant_total.toFixed(2).replace('.', ',')}€
                </div>
                {d.statut !== 'paye' && (
                  <button
                    onClick={() => handlePayer(d)}
                    disabled={actionLoading['pay-' + d.id]}
                    style={btnSmallPrimary}
                  >
                    {actionLoading['pay-' + d.id] ? '⏳' : '✓ Marquer payé'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const btnPrimary = {
  padding: '10px 16px',
  background: '#1D9E75',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: '500',
  cursor: 'pointer',
};

const btnSmallPrimary = {
  padding: '6px 12px',
  background: '#1D9E75',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '12px',
  cursor: 'pointer',
};

export default URSSAFSection;
